import { createReducer, on } from '@ngrx/store';

import { AuthModal } from '@shared/models/auth';
import { IUser } from '@shared/models/user';
import { IGraphQLError } from '@shared/models/graphql';

import {
  setLoadingAction,
  signInAction,
  signInActionSuccess,
  signInActionFailure,
  signInWithMagicLinkAction,
  signInWithMagicLinkActionFailure,
  signInWithEmailMagicLinkAction,
  signInWithEmailMagicLinkActionFailure,
  signInWithSmsMagicLinkAction,
  signInWithSmsMagicLinkActionFailure,
  signUpAction,
  signUpActionSuccess,
  signUpActionFailure,
  registerPasswordlessAction,
  registerPasswordlessActionFailure,
  updateAuthOrganizationAction,
  signOutAction,
  setupAccountAction,
  setupAccountActionSuccess,
  setupAccountActionFailure,
  updateUserPreferenceAction,
  updateUserPreferenceActionSuccess,
  updateUserPreferenceActionFailure,
  fetchUserAction,
  fetchUserActionSuccess,
  fetchUserActionFailure,
  updateUserConfirmedAtStatuses,
  clearRequestPasswordResetAction,
  requestPasswordResetAction,
  requestPasswordResetActionFinish,
  resetPasswordAction,
  resetPasswordActionFinish,
  setReferrerCodeAction,
  setEntityIdAction,
  setLastSMSSentAction,
  updatePhoneNumberSuccessAction,
  updateProfileAction,
  updateProfileActionSuccess,
  updateProfileActionFailure,
  updatePasswordAction,
  updatePasswordActionSuccess,
  updatePasswordActionFailure,
  setAuthModal,
  setAuthDefaultValues,
} from './auth.actions';

export interface AuthState {
  loading: boolean;
  loadingSelf?: boolean;
  errors?: IGraphQLError[];
  token?: string;
  user?: IUser;
  lastSMSSent?: string;
  requestPasswordReset?: {
    loading: boolean;
    email?: string;
  };
  resetPassword?: {
    loading: boolean;
  };
  updatePhoneNumberSuccess?: boolean;
  referrerCode: string | null;
  entityId: string | null;
  updateProfile?: {
    loading: boolean;
    errors?: IGraphQLError[];
    resource?: string;
    success: boolean;
  };
  authModal?: AuthModal;
  authDefaultValues?: {
    email: string | undefined;
    phoneNumber: string | undefined;
  };
}

export const initialState: AuthState = {
  loading: false,
  loadingSelf: false,
  errors: undefined,
  token: undefined,
  user: undefined,
  referrerCode: null,
  entityId: null,
  authModal: undefined,
};

export const authReducer = createReducer(
  initialState,
  on(setLoadingAction, (state, action) => ({
    ...state,
    loading: action.loading,
  })),
  on(signInAction, (state) => ({ ...state, loading: true })),
  on(signInActionSuccess, (state, action) => ({
    ...state,
    errors: undefined,
    token: action.token,
    loading: false,
    user: { ...action },
  })),
  on(signInActionFailure, (state) => ({
    ...state,
    loading: false,
    errors: undefined,
  })),
  on(signInWithMagicLinkAction, (state) => ({ ...state, loading: true })),
  on(signInWithMagicLinkActionFailure, (state) => ({
    ...state,
    loading: false,
    errors: undefined,
  })),

  on(signInWithEmailMagicLinkAction, (state) => ({ ...state, loading: true })),
  on(signInWithEmailMagicLinkActionFailure, (state) => ({
    ...state,
    loading: false,
    errors: undefined,
  })),

  on(signInWithSmsMagicLinkAction, (state) => ({ ...state, loading: true })),
  on(signInWithSmsMagicLinkActionFailure, (state) => ({
    ...state,
    loading: false,
    errors: undefined,
  })),

  on(signUpAction, (state) => ({ ...state, loading: true })),
  on(signUpActionSuccess, (state, action) => ({
    ...state,
    errors: undefined,
    token: action.metadata?.token,
    loading: false,
    user: { ...action.result },
  })),
  on(signUpActionFailure, (state, action) => ({
    ...state,
    loading: false,
    errors: action.errors,
  })),

  on(registerPasswordlessAction, (state) => ({ ...state, loading: true })),
  on(registerPasswordlessActionFailure, (state, action) => ({
    ...state,
    loading: false,
    errors: action.errors,
  })),

  on(updateAuthOrganizationAction, (state, action) =>
    state.user
      ? {
          ...state,
          user: {
            ...state.user,
            organization: { ...action.org },
          },
        }
      : state
  ),
  on(signOutAction, (state) => ({ ...state, ...initialState })),
  on(setupAccountAction, (state) => ({ ...state, loading: true })),
  on(setupAccountActionSuccess, (state, action) =>
    state.user
      ? {
          ...state,
          loading: false,
          errors: undefined,
          user: {
            ...state.user,
            preference: { ...action.preference },
            cbsaPreferences:
              !!action.cbsaPreferences && action.cbsaPreferences.length > 0
                ? action.cbsaPreferences.map((pref) => {
                    const parsed =
                      typeof pref === 'string' ? JSON.parse(pref) : pref;
                    return {
                      id: 'temp',
                      type: String(parsed.type || '').toUpperCase() as any,
                      preference: parsed.preference,
                      updatedAt: new Date().toISOString(),
                    };
                  })
                : [],
            /**
             * entityJson is like this: {"id":"0196fe16-a9cf-74d1-94b2-90f2c3f26670","full_name":"albert young"}
             * so we need to parse it to get the entityId and full_name
             */
            entities:
              action.entities?.map((entityJson) => {
                try {
                  const parsed = JSON.parse(entityJson);
                  return {
                    id: 'temp',
                    fullName: parsed.full_name || '',
                    entityId: parsed.id || '',
                  };
                } catch (error) {
                  // Fallback if JSON parsing fails
                  return {
                    id: 'temp',
                    fullName: '',
                    entityId: entityJson,
                  };
                }
              }) || [],
          },
        }
      : { ...state, loading: false }
  ),
  on(setupAccountActionFailure, (state, action) => ({
    ...state,
    loading: false,
    errors: action.errors,
  })),
  on(updateUserPreferenceAction, (state) => ({
    ...state,
    updateProfile: {
      loading: true,
      errors: undefined,
      resource: 'preferences',
      success: false,
    },
  })),
  on(updateUserPreferenceActionSuccess, (state, action) =>
    state.user
      ? {
          ...state,
          updateProfile: {
            loading: false,
            errors: undefined,
            resource: 'preferences',
            success: true,
          },
          user: {
            ...state.user,
            preference: { ...action.preference },
            cbsaPreferences:
              !!action.cbsaPreferences && action.cbsaPreferences.length > 0
                ? action.cbsaPreferences.map((pref) => {
                    const parsed =
                      typeof pref === 'string' ? JSON.parse(pref) : pref;
                    return {
                      id: 'temp',
                      type: String(parsed.type || '').toUpperCase() as any,
                      preference: parsed.preference,
                      updatedAt: new Date().toISOString(),
                    };
                  })
                : state.user.cbsaPreferences,
          },
        }
      : { ...state, updateProfile: { loading: false, success: true } }
  ),
  on(updateUserPreferenceActionFailure, (state, action) => ({
    ...state,
    updateProfile: {
      loading: false,
      errors: action.errors,
      resource: 'preferences',
      success: false,
    },
  })),
  on(fetchUserAction, (state, action) => ({
    ...state,
    loadingSelf: true,
  })),
  on(fetchUserActionSuccess, (state, action) => ({
    ...state,
    loading: false,
    loadingSelf: false,
    errors: undefined,
    user: { ...action },
  })),
  on(fetchUserActionFailure, (state, action) => ({
    ...state,
    loading: false,
    loadingSelf: false,
    errors: action.errors,
  })),

  on(updateUserConfirmedAtStatuses, (state, action) => ({
    ...state,
    user: state.user
      ? {
          ...state.user,
          emailConfirmedAt: action.emailConfirmedAt,
          phoneNumberConfirmedAt: action.phoneNumberConfirmedAt,
        }
      : undefined,
  })),
  on(clearRequestPasswordResetAction, (state) => ({
    ...state,
    requestPasswordReset: undefined,
  })),
  on(requestPasswordResetAction, (state, action) => ({
    ...state,
    requestPasswordReset: {
      loading: true,
      email: action.email,
    },
  })),
  on(requestPasswordResetActionFinish, (state, action) => ({
    ...state,
    requestPasswordReset: {
      loading: false,
      email: action.success ? state.requestPasswordReset?.email : undefined,
    },
  })),
  on(resetPasswordAction, (state) => ({
    ...state,
    resetPassword: { loading: true },
  })),
  on(resetPasswordActionFinish, (state) => ({
    ...state,
    resetPassword: { loading: false },
  })),
  on(setReferrerCodeAction, (state, action) => ({
    ...state,
    referrerCode: action.referrerCode,
  })),
  on(setEntityIdAction, (state, action) => ({
    ...state,
    entityId: action.entityId,
  })),
  on(setLastSMSSentAction, (state, action) => ({
    ...state,
    lastSMSSent: action.lastSMSSent,
  })),
  on(updatePhoneNumberSuccessAction, (state, action) => ({
    ...state,
    updatePhoneNumberSuccess: action.success,
    loading: false,
    user: { ...action.user },
  })),
  on(updateProfileAction, (state) => ({
    ...state,
    updateProfile: {
      loading: true,
      errors: undefined,
      resource: 'info',
      success: false,
    },
  })),
  on(updateProfileActionSuccess, (state, action) => ({
    ...state,
    updateProfile: {
      loading: false,
      errors: undefined,
      resource: undefined,
      success: true,
    },
    user: { ...action.updateProfile.result },
  })),
  on(updateProfileActionFailure, (state, action) => ({
    ...state,
    updateProfile: {
      loading: false,
      errors: action.errors,
      resource: undefined,
      success: false,
    },
  })),
  on(updatePasswordAction, (state) => ({
    ...state,
    updateProfile: {
      loading: true,
      errors: undefined,
      resource: 'password',
      success: false,
    },
  })),
  on(updatePasswordActionSuccess, (state, action) => ({
    ...state,
    updateProfile: {
      loading: false,
      errors: undefined,
      resource: 'password',
      success: true,
    },
  })),
  on(updatePasswordActionFailure, (state, action) => ({
    ...state,
    updateProfile: {
      loading: false,
      errors: action.errors,
      resource: undefined,
      success: false,
    },
  })),
  on(setAuthModal, (state, action) => ({
    ...state,
    authModal: action.authModal,
    authDefaultValues: {
      email: action.defaultEmail,
      phoneNumber: action.defaultPhoneNumber,
    },
  })),
  on(setAuthDefaultValues, (state, action) => ({
    ...state,
    authDefaultValues: {
      email: action.email,
      phoneNumber: action.phoneNumber,
    },
  }))
);
